import ItineraryPackageSet from '@itinerary/ItineraryPackageSet';
import type TripPackage from '@itinerary/trip/TripPackage';
import type TripPackageLoader from '@itinerary/trip/TripPackageLoader';
import { type TripPackageLoaderResult } from '@itinerary/trip/TripPackageLoader';
import {
    type FilterData,
    type FilterKey,
} from '@itinerary/ItineraryModuleTypes';
import { type Point, TripSortBy } from '@itinerary/trip/TripModuleTypes';
import { type TripPackageLoaderConfig } from '@itinerary/trip/types';

/**
 * Stores data related to a list of packages, that one of them must be chosen
 */
export default abstract class TripPackageSet<
    TFilterMetaData extends FilterData = FilterData,
    TFilterKey extends FilterKey = FilterKey,
    TPackage extends TripPackage = TripPackage,
> extends ItineraryPackageSet<TFilterMetaData, TFilterKey, TPackage> {
    /**
     * The package loader associated with this set
     */
    protected abstract override loader: TripPackageLoader<TPackage>;

    /**
     * The coordinates of the origin location, that is loaded along with the list of packages
     */
    originLocation: Point | null = null;

    /**
     * The coordinates of the destination location, that is loaded along with the list of packages
     */
    destinationLocation: Point | null = null;

    /**
     * Stores the current sorting method
     */
    override sortBy: TripSortBy = TripSortBy.Price;

    override onGenerate(result: TripPackageLoaderResult<TPackage>) {
        super.onGenerate(result);
        this.originLocation = result.originLocation;

        this.destinationLocation = result.destinationLocation;
        // Finding the minimum price of the list
        const minPrice = this.list
            .filter(
                item =>
                    item.defaultPrice?.singlePayable &&
                    item.capacity > 0 &&
                    item.reservable
            )
            .reduce((min: number | null, curr) => {
                if (min === null || min > curr.defaultPrice!.singlePayable) {
                    min = curr.defaultPrice!.singlePayable;
                }
                return min;
            }, null);

        // Setting the minimum price to be shown on the suggestion tab bar

        this.minPrice = minPrice ?? undefined;
    }

    get originName() {
        return this.loader.originName;
    }

    get destinationName() {
        return this.loader.destinationName;
    }

    get originCode() {
        return this.loader.originCode;
    }

    get destinationCode() {
        return this.loader.destinationCode;
    }

    protected override reset(
        config: TripPackageLoaderConfig,
        filters: Record<TFilterKey, any>,
        minPrice?: number
    ) {
        super.reset(config, filters, minPrice);
        this.sortBy = TripSortBy.Price;
        this.originLocation = null;
        this.destinationLocation = null;
    }

    protected constructor(
        /**
         * Contains filters that must be applied to the results
         */
        public override filters: Record<TFilterKey, any>,

        /**
         * Minimum price that is obtained or expected from the current package set
         */
        public override minPrice?: number,

        public override isSuggestion = false
    ) {
        super(filters, minPrice);
    }
}
